/**
 * UserNotifications' page side (docs/ops-protocol.md, "Notifications"). The
 * app schedules its own timers and sends a `notification` request when one
 * comes due; the page only answers for the permission and shows what it is
 * told to, with the browser's Notification API. Nothing is scheduled here, so
 * nothing fires once the page is closed.
 *
 * Remote notifications are Web Push ("Push notifications" there): the app's
 * `registerForRemoteNotifications` becomes `pushSubscribe`, which registers
 * the site's service worker (public/sw.js) and subscribes through it; the
 * subscription is the app's device token. The worker shows each push and
 * reports pushes and clicks to the page, which hands them to the app one
 * `receive` request at a time.
 */

import type { NotificationRequestParams } from './protocol';

/** `NotificationPermission`, or no Notification API at all. */
export type NotificationPermissionState = NotificationPermission | 'unsupported';

/** A push subscription as `PushSubscription.toJSON()` gives it: what a server sends to. */
export interface PushSubscriptionInfo {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/** What the service worker reports: a push that arrived, or a click on a notification it showed. */
export type PushEvent = { type: 'push'; payload: unknown } | { type: 'click'; payload: unknown; action: string };

export interface NotificationResult {
  /** `status` and `request`. */
  permission?: NotificationPermissionState;
  /** `show`: whether a notification appeared. */
  shown?: boolean;
  /** `pushSubscribe`. */
  subscription?: PushSubscriptionInfo;
  /** `receive`: the event's fields. */
  type?: PushEvent['type'];
  payload?: unknown;
  action?: string;
}

/** The browser's notification facilities; tests substitute them. */
export interface NotificationHost {
  permission(): NotificationPermissionState;
  requestPermission(): Promise<NotificationPermission>;
  /** Shows a notification; resolves with whether the browser showed it. */
  show(title: string, options: NotificationOptions): Promise<boolean>;
  /** Closes the shown notifications with these tags. */
  close(tags: readonly string[]): void;
  /** `navigator.setAppBadge` (0 clears), where the browser has it. */
  setBadge?(count: number): Promise<void>;
  /** Web Push through the service worker, where the browser has it. */
  push?: PushHost;
}

/** The push service, as the service worker reaches it; tests substitute it. */
export interface PushHost {
  /** Subscribes with the key (registering the worker first); the subscription. */
  subscribe(applicationServerKey: string): Promise<PushSubscriptionInfo>;
  unsubscribe(): Promise<void>;
  /** Resolves with the next push or click the worker reported. */
  receive(): Promise<PushEvent>;
}

export async function performNotification(params: NotificationRequestParams, host: NotificationHost | undefined): Promise<NotificationResult> {
  const permission = host?.permission() ?? 'unsupported';
  switch (params.action) {
    case 'status':
      return { permission };
    case 'request':
      // The browser asks only once; after that the answer stands until the
      // viewer changes it in the site settings.
      if (!host || permission !== 'default') return { permission };
      return { permission: await host.requestPermission() };
    case 'show': {
      if (params.badge !== undefined) await host?.setBadge?.(params.badge).catch(() => undefined);
      if (params.title === undefined || !host || permission !== 'granted') return { shown: false };
      const options: NotificationOptions = { silent: params.silent ?? false };
      const body = [params.subtitle, params.body].filter((line) => line).join('\n');
      if (body) options.body = body;
      if (params.id) options.tag = params.id;
      return { shown: await host.show(params.title, options) };
    }
    case 'close':
      host?.close(params.ids ?? []);
      return {};
    case 'badge':
      await host?.setBadge?.(params.badge ?? 0).catch(() => undefined);
      return {};
    case 'pushSubscribe': {
      if (!host?.push) throw new Error('unsupported: this browser has no push service');
      if (!params.applicationServerKey) throw new Error('push: no application server key (webPushKey in swiftbrowser.json)');
      // A subscription needs the permission: asked for here when it has not been.
      const granted = permission === 'default' ? await host.requestPermission() : permission;
      if (granted !== 'granted') throw new Error('denied: notifications are not allowed');
      return { subscription: await host.push.subscribe(params.applicationServerKey) };
    }
    case 'pushUnsubscribe':
      await host?.push?.unsubscribe();
      return {};
    case 'receive': {
      if (!host?.push) throw new Error('unsupported: this browser has no push service');
      const event = await host.push.receive();
      return event.type === 'click' ? { type: 'click', payload: event.payload, action: event.action } : { type: 'push', payload: event.payload };
    }
    default:
      throw new Error(`unsupported: notification action ${String((params as { action: unknown }).action)}`);
  }
}

/** The Notification API, or nothing where the browser has none (an iPhone tab not on the Home Screen). */
export function browserNotificationHost(push?: PushHost): NotificationHost | undefined {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return undefined;
  const open = new Map<string, Notification>();
  const badgeNavigator = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  return {
    permission: () => Notification.permission,
    requestPermission: () =>
      new Promise((resolve) => {
        // Older Safari takes a callback and returns nothing.
        const result = Notification.requestPermission((answer) => resolve(answer));
        if (result) result.then(resolve, () => resolve(Notification.permission));
      }),
    show: async (title, options) => {
      try {
        const notification = new Notification(title, options);
        if (options.tag) open.set(options.tag, notification);
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
        notification.onclose = () => {
          if (options.tag && open.get(options.tag) === notification) open.delete(options.tag);
        };
        return true;
      } catch {
        // Chrome on Android shows notifications only through a service worker.
        const registration = await navigator.serviceWorker?.getRegistration().catch(() => undefined);
        if (!registration) return false;
        await registration.showNotification(title, options);
        return true;
      }
    },
    close: (tags) => {
      for (const tag of tags) {
        open.get(tag)?.close();
        open.delete(tag);
      }
    },
    ...(typeof badgeNavigator.setAppBadge === 'function'
      ? {
          setBadge: (count: number) =>
            count > 0 ? badgeNavigator.setAppBadge!(count) : (badgeNavigator.clearAppBadge?.() ?? Promise.resolve()),
        }
      : {}),
    ...(push ? { push } : {}),
  };
}

/** The message types the service worker (public/sw.js) posts to the page. */
export const PUSH_MESSAGE = 'swiftbrowser:push';
export const CLICK_MESSAGE = 'swiftbrowser:notificationclick';

/** The push event a service worker message carries, or null when `data` is not one. */
export function pushEventOf(data: unknown): PushEvent | null {
  if (typeof data !== 'object' || data === null) return null;
  const message = data as { type?: unknown; payload?: unknown; action?: unknown };
  if (message.type === PUSH_MESSAGE) return { type: 'push', payload: message.payload ?? {} };
  if (message.type === CLICK_MESSAGE) return { type: 'click', payload: message.payload ?? {}, action: typeof message.action === 'string' ? message.action : '' };
  return null;
}

/** A VAPID public key (base64url, as servers print it) as the bytes `subscribe` takes. */
export function applicationServerKeyBytes(key: string): Uint8Array<ArrayBuffer> {
  const base64 = key.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** The subscription's `toJSON()`, checked for the fields a server needs. */
function subscriptionInfo(subscription: PushSubscription): PushSubscriptionInfo {
  const json = subscription.toJSON();
  const keys = json.keys ?? {};
  if (!json.endpoint || typeof keys['p256dh'] !== 'string' || typeof keys['auth'] !== 'string') throw new Error('push: the subscription has no keys');
  return { endpoint: json.endpoint, keys: { p256dh: keys['p256dh'], auth: keys['auth'] } };
}

/**
 * Web Push through the service worker at `workerURL` with `scope` (the
 * site's base), or nothing where the browser has no push service (Safari
 * before 16.4; an iPhone tab not on the Home Screen). Listens for the
 * worker's messages from the start: a click that opened the page is posted
 * before the app asks for it.
 */
export function browserPushHost(workerURL: string, scope: string): PushHost | undefined {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !navigator.serviceWorker || typeof PushManager === 'undefined') return undefined;
  const container = navigator.serviceWorker;
  const queue: PushEvent[] = [];
  const waiting: ((event: PushEvent) => void)[] = [];
  container.addEventListener('message', (message: MessageEvent) => {
    const event = pushEventOf(message.data);
    if (!event) return;
    const wake = waiting.shift();
    if (wake) wake(event);
    else queue.push(event);
  });
  const registration = async (): Promise<ServiceWorkerRegistration> => {
    const existing = await container.getRegistration(scope);
    if (existing?.active && existing.scope === new URL(scope, location.href).href) return existing;
    return container.register(workerURL, { scope });
  };
  return {
    subscribe: async (key) => {
      const worker = await registration();
      await container.ready;
      const options: PushSubscriptionOptionsInit = { userVisibleOnly: true, applicationServerKey: applicationServerKeyBytes(key) };
      const current = await worker.pushManager.getSubscription();
      try {
        return subscriptionInfo(current ?? (await worker.pushManager.subscribe(options)));
      } catch (error) {
        // A subscription under another key stands in the way: replaced.
        if (!current) throw error;
        await current.unsubscribe().catch(() => undefined);
        return subscriptionInfo(await worker.pushManager.subscribe(options));
      }
    },
    unsubscribe: async () => {
      const worker = await container.getRegistration(scope);
      const current = await worker?.pushManager.getSubscription();
      await current?.unsubscribe();
    },
    receive: () => {
      const queued = queue.shift();
      if (queued) return Promise.resolve(queued);
      return new Promise((resolve) => waiting.push(resolve));
    },
  };
}
